import type Plan from '@/classes/Plan'
import { DeckFlavor } from '@/enums/Language'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { usePaymentOptions } from '@/mixins/paymentOptions'
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import { deepToRaw } from '@/services/deepToRaw'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import { useRetryMethods } from '@/services/retryUtils'
import type { SubmissionService } from '@/services/types'
import { useContentStore } from '@/stores/content'
import { useCrmStore } from '@/stores/crm'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import type { InsuranceScenarioOutput } from '@/types'
import { captureException } from '@sentry/vue'
import { type AxiosRequestConfig } from 'axios'
import localforageLib from 'localforage'

const storeName = 'emailReceiptSubmissions'

export type EmailFuneralHome = {
	id?: string
	logoUrl: string | null | undefined
	name?: string
}

export type EmailAddOn = {
	included: boolean
	price: number | undefined
}

export type EmailReceiptRequest = {
	planId: string
	planEmail: string
	funeralHome: EmailFuneralHome
	insuranceScenarioOutput: InsuranceScenarioOutput
	everplans: EmailAddOn
	oap: EmailAddOn
}

const localEmailReceiptSubmissions = localforageLib.createInstance({
	name: 'request-queue',
	storeName,
})

const emailReceiptSubmissionQueueConfig = {
	axiosInstance: purchaseApiAxiosInstance,
	requestBackgroundSync: async (tag: string) => {
		await windowRequestQueueConfig.requestBackgroundSync(tag)
	},
}

const emailReceiptSubmissionRequestQueue = useRequestQueue(
	storeName,
	emailReceiptSubmissionQueueConfig
)

export const getQueue = () => {
	return emailReceiptSubmissionRequestQueue
}

// This method handles creating an email submission request and adding it to the queue
export const submitRequest = async (planHolder: Plan) => {
	if (!shouldSubmit(planHolder)) {
		return
	}

	const data = await gatherEmailSubmissionData(planHolder)

	const emailReceiptRequestConfig: AxiosRequestConfig = {
		url: `${import.meta.env.VITE_PURCHASE_API_URL}/api/v1/send-receipt`,
		method: 'POST',
		withCredentials: true,
		data,
	}

	try {
		await emailReceiptSubmissionRequestQueue.addRequest(
			planHolder.id,
			emailReceiptRequestConfig
		)
	} catch (error: any) {
		console.log(error)

		// only log to sentry, executeRequest takes care of updating appropriate status in the localForage
		captureException(error, {
			extra: {
				message: `Error submitting email receipt.`,
				response: error.response?.data,
			},
		})
	}
}

const gatherEmailSubmissionData = async (
	planHolder: Plan
): Promise<EmailReceiptRequest> => {
	const funeralHome = await getFuneralHome(planHolder)

	const insuranceScenarioOutput = await getInsuranceScenarioOutput(planHolder)

	const { getPlanById } = useOapPlanProvider(planHolder.oapPayment.provider)
	const oap = getPlanById(planHolder.oapPayment.paymentPlanId)

	return {
		planId: planHolder.id,
		planEmail: planHolder.email,
		funeralHome: {
			id: funeralHome.id,
			logoUrl: funeralHome?.logoUrl,
			name: funeralHome.name,
		},
		insuranceScenarioOutput: insuranceScenarioOutput,
		everplans: {
			included: planHolder.everplansIncluded,
			price: planHolder.everplansPayment.amount,
		},
		oap: {
			included: planHolder.oapIncluded,
			price: oap?.price,
		},
	}
}

const getFuneralHome = async (planHolder: Plan): Promise<EmailFuneralHome> => {
	const crmStore = useCrmStore()
	const contentStore = useContentStore()

	const planFuneralHome = crmStore.findFuneralHome(planHolder.funeralHomeId)
	const planFuneralHomeMedia = planFuneralHome?.crmGroupId
		? await contentStore.getCombinedFhLocation(
				planFuneralHome,
				DeckFlavor.Default
			)
		: undefined

	return {
		id: planFuneralHome?.id,
		logoUrl: planFuneralHomeMedia?.logo,
		name: planFuneralHome?.shortName || planFuneralHome?.name,
	}
}

// This method gets the insurance payment data for the email and updates this data on the plan
const getInsuranceScenarioOutput = async (
	planHolder: Plan
): Promise<InsuranceScenarioOutput> => {
	const sessionStore = useSessionStore()

	const planInsuranceScenarioOutput = {
		...structuredClone(deepToRaw(planHolder.insuranceScenarioOutput)),
	}

	// If there isn't an active session; return without updating plan
	if (!sessionStore.activeSession) {
		return planInsuranceScenarioOutput
	}

	const insuranceScenarioOutputFromPaymentOptions =
		getInsuranceScenarioOutputFromPaymentOptions(planHolder)

	const areDeepEqual =
		JSON.stringify(deepToRaw(insuranceScenarioOutputFromPaymentOptions)) ===
		JSON.stringify(planInsuranceScenarioOutput)

	// If the active session matches the plan; return without updating store
	if (areDeepEqual) {
		return planInsuranceScenarioOutput
	}

	// otherwise update the store and return data from payment options
	const planStore = usePlanStore()

	await planStore.updatePlan(planHolder.id, {
		insuranceScenarioOutput: insuranceScenarioOutputFromPaymentOptions,
	})

	return insuranceScenarioOutputFromPaymentOptions
}

const getInsuranceScenarioOutputFromPaymentOptions = (
	planHolder: Plan
): InsuranceScenarioOutput => {
	const { getPlanPaymentOptions } = usePaymentOptions()

	const paymentOptions = getPlanPaymentOptions(planHolder)

	const filteredPaymentSummary =
		paymentOptions.paymentSummaryOptions.value.filter(
			(item) => item.label !== 'Due Today'
		)

	return {
		planTerm: paymentOptions.scenario.value?.term,
		totalCost: paymentOptions.totalFuneralAmount.value,
		paidToday: paymentOptions.totalDueTodayAmount.value,
		firstPayment: paymentOptions.firstPaymentAmount.value,
		downPayment: planHolder.insurance.downPayment,
		paymentSummary: filteredPaymentSummary,
	}
}

const shouldSubmit = (plan: Plan): boolean => {
	// don't email people without emails
	return !!plan.email
}

export const emailReceiptService: SubmissionService = {
	getQueue,
	submitRequest,
	// Validation is not needed for Email.
	validateRequest: () => true,
	serviceName: 'Email',
	storeName,
	shouldSubmit,
	...useRetryMethods(
		emailReceiptSubmissionRequestQueue,
		localEmailReceiptSubmissions,
		'Email'
	),
}
